import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Users, Plus, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const STATUSES = ["new", "contacted", "qualified", "won", "lost"];

export default function CRM() {
  const [leads, setLeads] = useState([]);
  const [open, setOpen] = useState(false);
  const empty = { name: "", email: "", phone: "", company: "", status: "new", source: "manual", interest: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/crm/leads").then((r) => setLeads(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/crm/leads", form);
      toast.success("Lead added");
      setOpen(false); setForm(empty); load();
    } catch { toast.error("Failed"); } finally { setBusy(false); }
  };

  const updateStatus = async (lead, status) => {
    await api.put(`/crm/leads/${lead.id}`, { ...lead, status });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/crm/leads/${id}`); toast.success("Deleted"); load();
  };

  return (
    <div data-testid="crm-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-extrabold text-3xl">CRM</h1>
          <p className="text-sm text-[#4B5563]">Leads captured by your agents and your team.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button className="btn-primary flex items-center gap-2" data-testid="new-lead-button"><Plus className="w-4 h-4" /> New lead</button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add lead</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              {["name", "email", "phone", "company", "interest"].map((k) => (
                <div key={k} className={k === "interest" ? "col-span-2" : ""}>
                  <label className="text-xs font-medium block mb-1 capitalize">{k}</label>
                  <input data-testid={`lead-${k}-input`} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full border border-[#E2E8F0] px-2 py-2 rounded-sm text-sm" />
                </div>
              ))}
              <div className="col-span-2">
                <label className="text-xs font-medium block mb-1">Notes</label>
                <textarea data-testid="lead-notes-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border border-[#E2E8F0] px-2 py-2 rounded-sm text-sm" />
              </div>
            </div>
            <button data-testid="lead-save-button" onClick={save} disabled={busy || !form.name} className="btn-primary w-full mt-4">{busy ? "Saving…" : "Save"}</button>
          </DialogContent>
        </Dialog>
      </div>

      {leads.length === 0 ? (
        <div className="hs-card p-12 text-center">
          <Users className="w-10 h-10 mx-auto text-[#4B5563]" />
          <div className="font-display font-bold mt-4">No leads yet</div>
          <div className="text-sm text-[#4B5563]">Add a lead manually or let your agents capture them.</div>
        </div>
      ) : (
        <div className="hs-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F3F4F6] text-[11px] uppercase font-mono text-[#4B5563]">
              <tr>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">Interest</th>
                <th className="text-left px-4 py-3">Source</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-t border-[#E2E8F0]" data-testid={`lead-row-${l.id}`}>
                  <td className="px-4 py-3 font-medium">{l.name}</td>
                  <td className="px-4 py-3 text-xs"><div>{l.email}</div><div className="font-mono">{l.phone}</div></td>
                  <td className="px-4 py-3 text-xs">{l.interest || "—"}</td>
                  <td className="px-4 py-3 text-xs">{l.source}</td>
                  <td className="px-4 py-3">
                    <select value={l.status} onChange={(e) => updateStatus(l, e.target.value)} data-testid={`lead-status-${l.id}`} className="border border-[#E2E8F0] px-2 py-1 rounded-sm text-xs bg-white">
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(l.id)} className="text-[#EF4444] p-1" data-testid={`lead-delete-${l.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
